import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import toast, { type Toast } from "react-hot-toast";
import { CheckCheck, Reply, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLocale } from "@/hooks/useLocale";
import { useAppSelector } from "@/hooks/useReduxHooks";
import { selectUser } from "@/store/slices/user.slice";
import { MS_CHAT_PATHS } from "@/routes/my-space/routes.chat";
import { useMarkChatReadMutation } from "@/services/(my-space)/api.chat";
import { CHAT_LOCAL_NOTIFY, chatHub, type IChatNotifyPayload } from "@/services/websocket/chat-hub";
import { cn } from "@/utils/cn";
import { ChatAvatar } from "@/pages/(home)/(my-space)/chat/_components/ChatAvatar";
import {
  conversationTitle,
  messageContentPreview,
  personName,
} from "@/pages/(home)/(my-space)/chat/_lib/chat-labels";
import { QuickReplyBox } from "@/pages/(home)/(my-space)/chat/_components/QuickReplyBox";
import { openChat } from "@/components/layout/chat-windows/chat-windows-store";
import { channelOf } from "@/pages/(home)/(my-space)/chat/_lib/channel-notices";

/** How long a popup stays (hovering pauses it, and its countdown bar). */
const POPUP_MS = 6000;

const popupId = (conversationId: number) => `chat-message-${conversationId}`;

/** A popup on screen: what it shows, and whether a reply is being typed in it. */
interface PopupState {
  payload: IChatNotifyPayload;
  count: number;
  at: number;
  replying: boolean;
}

/**
 * Bottom-right popups for new chat messages, like WhatsApp Web's: who, where,
 * what — Open, Reply right there, or Mark as read without leaving the page.
 * One per chat: a newer message replaces it and counts up. Shown by the same
 * rules as the sound (`shouldNotify`), and never for the chat that's already
 * open — even with the tab in the background, it's right there. While a reply
 * is being typed the popup stays put.
 */
export const ChatMessagePopups = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const popups = useRef(new Map<number, PopupState>());

  const settle = useCallback((id: number) => {
    toast.dismiss(popupId(id));
    popups.current.delete(id);
  }, []);

  const show = useCallback(
    (id: number) => {
      const p = popups.current.get(id);
      if (!p) return;
      toast.custom(
        (tt) => (
          <ChatMessagePopup
            toast={tt}
            payload={p.payload}
            count={p.count}
            replying={p.replying}
            onReplying={(replying) => {
              const cur = popups.current.get(id);
              if (!cur || cur.replying === replying) return;
              // Giving up on a reply gives the popup a fresh countdown.
              popups.current.set(id, { ...cur, replying, at: Date.now() });
              show(id);
            }}
            onOpen={() => {
              settle(id);
              // A floating window where there's room; else the Chat page.
              openChat(id, {
                onChatPage:
                  window.location.pathname === MS_CHAT_PATHS.ROOT ||
                  window.location.pathname.startsWith(`${MS_CHAT_PATHS.ROOT}/`),
                goToChat: () => navigate(MS_CHAT_PATHS.CONVERSATION(id)),
              });
            }}
            onClose={() => settle(id)}
          />
        ),
        {
          id: popupId(id),
          position: "bottom-right",
          duration: p.replying ? Infinity : POPUP_MS,
        },
      );
    },
    [navigate, settle],
  );

  useEffect(
    () =>
      chatHub.subscribe((event, payload) => {
        if (event !== CHAT_LOCAL_NOTIFY) return;
        const { message, conversation, reaction } = payload as IChatNotifyPayload;
        const id = message.conversationId;
        if (chatHub.isOpen(id)) return;
        const prev = popups.current.get(id);
        const now = Date.now();
        // A popup that has already gone starts over at one.
        const live = prev && (prev.replying || now - prev.at < POPUP_MS);
        popups.current.set(id, {
          payload: {
            message,
            conversation: conversation ?? prev?.payload.conversation,
            reaction,
          },
          // A reaction isn't a new message: it doesn't add to the count.
          count: live && !reaction ? prev.count + 1 : 1,
          at: now,
          replying: live ? prev.replying : false,
        });
        show(id);
      }),
    [show],
  );

  // Opening the chat (from anywhere) settles its popup.
  useEffect(() => {
    const match = pathname.match(/\/chat\/(\d+)/);
    if (match) settle(Number(match[1]));
  }, [pathname, settle]);

  return null;
};

const ChatMessagePopup = ({
  toast: tt,
  payload: { message: m, conversation: c, reaction },
  count,
  replying,
  onReplying,
  onOpen,
  onClose,
}: {
  toast: Toast;
  payload: IChatNotifyPayload;
  count: number;
  replying: boolean;
  onReplying: (replying: boolean) => void;
  onOpen: () => void;
  onClose: () => void;
}) => {
  const { t } = useTranslation("chat");
  const { formatDate } = useLocale();
  const [markRead, { isLoading: marking }] = useMarkChatReadMutation();
  // A reaction is from whoever reacted, about my message.
  const sender = personName(t, reaction ? reaction.user : m.sender);
  const senderImage = reaction ? reaction.user?.profileImage : m.sender?.profileImage;
  // A channel's notice: titled by the channel, nobody "sent" it.
  const isChannel = c?.type === "channel" || m.type === "notice";
  const isGroup = !!c && c.type !== "direct" && !isChannel;
  const title = isGroup || (isChannel && c) ? conversationTitle(t, c!) : sender;
  const preview = messageContentPreview(t, {
    type: m.type,
    body: m.body,
    metadata: m.metadata,
    attachmentCount: m.attachments.length,
    isDeleted: m.isDeleted,
  });
  const content = reaction ? t("popup.reacted_to", { emoji: reaction.emoji, content: preview }) : preview;
  const myUserId = useAppSelector(selectUser).user?.id;
  const tagsMe = !reaction && (m.metadata?.mentions ?? []).some((x) => x.userId === myUserId);
  // Not cached yet: let the server decide. Broadcast recipients can't answer.
  const canReply = c?.canSend ?? c?.type !== "broadcast";

  const markAsRead = async () => {
    try {
      await markRead({ id: m.conversationId, messageId: m.id }).unwrap();
    } finally {
      onClose();
    }
  };

  return (
    <div
      style={{ "--popup-duration": `${POPUP_MS}ms` } as React.CSSProperties}
      className={cn(
        "group/popup pointer-events-auto relative w-[360px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-border/70 bg-background shadow-xl ring-1 ring-black/5 transition-all duration-300 ease-out",
        tt.visible ? "translate-x-0 opacity-100" : "translate-x-6 opacity-0 rtl:-translate-x-6",
      )}
    >
      {/* Eyebrow: what this is and when. */}
      <div className="flex items-center gap-1.5 px-4 pt-3">
        <img
          src="/icons/interface-icons/shared/icon-3d-chat.png"
          alt=""
          draggable={false}
          className="size-4 object-contain"
        />
        <span className="text-[11px] font-medium text-muted-foreground">
          {reaction ? t("popup.reacted") : tagsMe ? t("popup.mentioned_you") : t("popup.new_message")}
        </span>
        <span aria-hidden className="text-[11px] text-muted-foreground">
          ·
        </span>
        <span className="text-[11px] text-muted-foreground">
          {formatDate(new Date(reaction?.at ?? m.createdAt), "p")}
        </span>
        <Button
          variant="ghost"
          size="icon-xs"
          onClick={onClose}
          aria-label={t("popup.dismiss")}
          className="-me-1.5 ms-auto size-6"
        >
          <X className="size-3.5" />
        </Button>
      </div>

      {/* Who, where, what — the whole block opens the chat. */}
      <button
        type="button"
        onClick={onOpen}
        className="flex w-full items-start gap-3 px-4 pt-2 pb-3 text-start"
        aria-label={t("popup.open", { name: title })}
      >
        <span className="relative shrink-0">
          <ChatAvatar
            name={title}
            imageUrl={isGroup ? c?.imageUrl : isChannel ? null : senderImage}
            kind={isChannel ? "channel" : isGroup ? c?.type : "direct"}
            channel={channelOf(c) ?? m.metadata?.notice?.channel}
            size="lg"
          />
          {isGroup ? (
            // A group's picture, with whoever wrote on its corner.
            <span className="absolute -end-1 -bottom-1 rounded-full ring-2 ring-background">
              <ChatAvatar name={sender} imageUrl={senderImage} size="xs" />
            </span>
          ) : null}
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="flex items-center gap-2">
            <span className="truncate text-sm font-semibold text-foreground">{title}</span>
            {count > 1 ? (
              <span className="shrink-0 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] leading-none font-semibold text-primary">
                {t("popup.new_messages", { count })}
              </span>
            ) : null}
          </span>
          {isGroup ? (
            <span className="truncate text-xs font-medium text-foreground/80">{sender}</span>
          ) : null}
          <span
            dir="auto"
            className="line-clamp-2 text-[13px] leading-snug break-words text-muted-foreground"
          >
            {content}
          </span>
        </span>
      </button>

      {replying ? (
        <QuickReplyBox
          conversationId={m.conversationId}
          name={title}
          // A newer message re-renders the popup: keep typing where you were.
          focusKey={m.id}
          onSent={onClose}
          onCancel={() => onReplying(false)}
          className="border-t border-border/60 bg-muted/30 px-3 py-2"
        />
      ) : (
        <div className="flex items-center justify-end gap-1.5 border-t border-border/60 bg-muted/30 px-3 py-2">
          <Button variant="ghost" size="xs" onClick={() => void markAsRead()} isLoading={marking}>
            <CheckCheck className="size-3.5" />
            {t("popup.mark_read")}
          </Button>
          {canReply ? (
            <Button variant="ghost" size="xs" onClick={() => onReplying(true)}>
              <Reply className="size-3.5" />
              {t("popup.reply")}
            </Button>
          ) : null}
          <Button variant="primary" size="xs" onClick={onOpen}>
            {t("popup.open_chat")}
          </Button>
        </div>
      )}

      {/* Time left; the toaster pauses on hover, so does the bar. A reply holds it. */}
      {replying ? null : (
        <span
          // Keyed by the message, so a newer one restarts the countdown.
          key={m.id}
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-0.5 origin-left animate-popup-countdown bg-primary group-hover/popup:[animation-play-state:paused] rtl:origin-right"
        />
      )}
    </div>
  );
};
